import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { loginWithEmail } from '../store/slices/authSlice'
import useSystemSettings from '../hooks/useSystemSettings'

const Login = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })
  const { error, loading, isAuthenticated } = useAppSelector(state => state.auth)
  const { getSetting } = useSystemSettings()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = searchParams.get('redirect') || '/'

  // 如果已經登入，重定向到目標頁面
  useEffect(() => {
    console.log('Login page useEffect - isAuthenticated:', isAuthenticated, 'redirectTo:', redirectTo)
    if (isAuthenticated) {
      console.log('Redirecting to:', redirectTo)
      navigate(redirectTo, { replace: true })
    }
  }, [isAuthenticated, navigate, redirectTo])

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      console.log('Attempting login with redirect to:', redirectTo)
      const result = await dispatch(loginWithEmail(formData))
      console.log('Login result:', result)
      if (loginWithEmail.fulfilled.match(result)) {
        console.log('Login successful, redirecting to:', redirectTo)
        // 登入成功後立即重定向
        navigate(redirectTo, { replace: true })
      }
    } catch (error) {
      console.error('Login failed:', error)
    }
  }

  const handleGoogleLogin = async () => {
    setIsLoading(true)
    try {
      // 實作真正的 Google OAuth - 使用當前視窗重新導向
      // 使用 redirectTo 而不是當前完整路徑，避免循環重定向
      const authUrl = `https://accounts.google.com/o/oauth2/auth?client_id=${import.meta.env.VITE_GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/google/callback')}&scope=openid%20profile%20email&response_type=code&access_type=offline&prompt=consent&state=${encodeURIComponent(redirectTo)}`
      
      // 直接重新導向到 Google 授權頁面
      window.location.href = authUrl
      
    } catch (error) {
      console.error('Login failed:', error)
      setIsLoading(false)
    }
  }

  const handleLineLogin = async () => {
    setIsLoading(true)
    try {
      // 實作 LINE Login - 使用當前視窗重新導向
      // 使用 redirectTo 而不是當前完整路徑，避免循環重定向
      const state = encodeURIComponent(redirectTo)
      const nonce = Math.random().toString(36).substring(2, 15)
      const authUrl = `https://access.line.me/oauth2/v2.1/authorize?response_type=code&client_id=${import.meta.env.VITE_LINE_CLIENT_ID}&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/line/callback')}&state=${state}&scope=profile%20openid%20email&nonce=${nonce}`
      
      // 直接重新導向到 LINE 授權頁面
      window.location.href = authUrl
      
    } catch (error) {
      console.error('LINE Login failed:', error)
      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            登入 {getSetting('system.title', 'MoniJira')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            現代化的專案管理工具
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleGoogleLogin}
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                    <path
                      fill="currentColor"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="currentColor"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="currentColor"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                  使用 Google 帳號登入
                </>
              )}
            </button>

            <div className="space-y-3">
              <button
                onClick={handleLineLogin}
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-gray-300 text-sm font-medium rounded-md text-white bg-green-500 hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <>
                    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19.365 9.863c.349 0 .63.285.63.631 0 .345-.281.63-.63.63H17.61v1.125h1.755c.349 0 .63.283.63.63 0 .344-.281.629-.63.629h-2.386c-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63h2.386c.346 0 .627.285.627.63 0 .349-.281.63-.63.63H17.61v1.125h1.755zm-3.855 3.016c0 .27-.174.51-.432.596-.064.021-.133.031-.199.031-.211 0-.391-.09-.51-.25l-2.443-3.317v2.94c0 .344-.279.629-.631.629-.346 0-.626-.285-.626-.629V8.108c0-.27.173-.51.43-.595.06-.023.136-.033.194-.033.195 0 .375.104.495.254l2.462 3.33V8.108c0-.345.282-.63.63-.63.345 0 .63.285.63.63v4.771zm-5.741 0c0 .344-.282.629-.631.629-.345 0-.627-.285-.627-.629V8.108c0-.345.282-.63.63-.63.346 0 .628.285.628.63v4.771zm-2.466.629H4.917c-.345 0-.63-.285-.63-.629V8.108c0-.345.285-.63.63-.63.348 0 .63.285.63.63v4.141h1.756c.348 0 .629.283.629.63 0 .344-.282.629-.629.629M24 10.314C24 4.943 18.615.572 12 .572S0 4.943 0 10.314c0 4.811 4.27 8.842 10.035 9.608.391.082.923.258 1.058.59.12.301.079.766.038 1.08l-.164 1.02c-.045.301-.24 1.186 1.049.645 1.291-.539 6.916-4.078 9.436-6.975C23.176 14.393 24 12.458 24 10.314"/>
                    </svg>
                    使用 LINE 帳號登入
                  </>
                )}
              </button>
              
              {/* Privacy Notice for LINE Login */}
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-800">
                <div className="flex items-start">
                  <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <div className="font-medium mb-1">資料使用說明</div>
                    <p className="text-xs leading-relaxed">
                      當您使用 Google / LINE 登入時，我們將：
                    </p>
                    <ul className="mt-1 ml-3 text-xs space-y-0.5">
                      <li>• 取得您的基本個人資料（*僅姓名、頭像、Email）用於帳戶識別</li>
                      <li>• 取得您的 Google / 
                        <span 
                          className="relative group cursor-help underline decoration-dotted"
                          title="LINE ID 說明"
                        >
                          LINE 內部識別碼
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            此 LINE 內部識別碼並非登入 LINE 用的手機或帳號，
                            <br />
                            由LY Corporation(Line母公司)所指派，僅限服務供應商使用的獨特用戶識別代碼，
                            <br />
                            只用於識別本服務的帳號
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                          </div>
                        </span>
                         用於建立唯一帳戶</li>
                      <li>• 在此登入不會取得其他服務的任何資料與任何存取權限</li>
                      <li>• 所有資料僅用於本服務，不會分享給第三方</li>
                    </ul>
                    <p className="mt-2 text-xs">
                      點擊「使用 Google / LINE 帳號登入」即表示您同意我們按照上述說明使用您的資料。詳細資訊請參閱
                      <Link 
                        to="/privacy-policy" 
                        className="text-blue-600 hover:text-blue-800 underline ml-1"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        隱私權政策
                      </Link>
                      。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-50 text-gray-500">或</span>
            </div>
          </div>

          <form onSubmit={handleEmailLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="sr-only">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Email 地址"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                密碼
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleInputChange}
                className="appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="密碼"
              />
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  '登入'
                )}
              </button>
            </div>
          </form>

          {/* Footer Links */}
          <div className="text-center text-xs text-gray-500 space-y-2">
            <div>
              使用本服務即表示您同意我們的 
              <Link 
                to="/privacy-policy" 
                className="text-blue-600 hover:text-blue-800 underline ml-1"
              >
                隱私權政策
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
